const DB_NAME = "egs-app-offline";
const DB_VERSION = 1;
const encoder = new TextEncoder();
const decoder = new TextDecoder();

let dbPromise;
let cryptoKeyPromise;
let activeScope = "";

function openDatabase() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains("records")) db.createObjectStore("records", { keyPath: "id" });
      if (!db.objectStoreNames.contains("keys")) db.createObjectStore("keys", { keyPath: "id" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

async function storeRequest(storeName, mode, operation) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const request = operation(transaction.objectStore(storeName));
    transaction.oncomplete = () => resolve(request instanceof IDBRequest ? request.result : request);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error || new Error("Offline storage transaction aborted"));
  });
}

async function cryptoKey() {
  if (cryptoKeyPromise) return cryptoKeyPromise;
  cryptoKeyPromise = (async () => {
    const existing = await storeRequest("keys", "readonly", (store) => store.get("local-encryption-key"));
    if (existing?.key) return existing.key;
    const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
    await storeRequest("keys", "readwrite", (store) => store.put({ id: "local-encryption-key", key }));
    return key;
  })();
  return cryptoKeyPromise;
}

function encodeBase64(value) {
  return btoa(String.fromCharCode(...new Uint8Array(value)));
}

function decodeBase64(value) {
  return Uint8Array.from(atob(value), (character) => character.charCodeAt(0));
}

async function seal(value) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, await cryptoKey(), encoder.encode(JSON.stringify(value)));
  return { iv: encodeBase64(iv), data: encodeBase64(encrypted) };
}

async function unseal(record) {
  const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv: decodeBase64(record.iv) }, await cryptoKey(), decodeBase64(record.data));
  return JSON.parse(decoder.decode(plaintext));
}

async function derivePasswordVerifier(password, salt) {
  const material = await crypto.subtle.importKey("raw", encoder.encode(password), "PBKDF2", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits({ name: "PBKDF2", hash: "SHA-256", salt, iterations: 210000 }, material, 256);
  return new Uint8Array(bits);
}

function matchesVerifier(left, right) {
  if (left.length !== right.length) return false;
  let mismatch = 0;
  for (let index = 0; index < left.length; index += 1) mismatch |= left[index] ^ right[index];
  return mismatch === 0;
}

function notifyOfflineState() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event("egs-offline-state"));
}

function makeScope(user, tenant) {
  return `${tenant?.tenant_id || "no-tenant"}:${user?.user_id || "no-user"}`;
}

export function setOfflineIdentity(user, tenant) {
  activeScope = user?.user_id ? makeScope(user, tenant) : "";
}

export function getOfflineScope() {
  return activeScope;
}

export async function saveOfflineSession(session) {
  if (!session?.user?.user_id) return;
  const offlineSession = { ...session };
  delete offlineSession.access_token;
  const encrypted = await seal({ ...offlineSession, saved_at: new Date().toISOString() });
  await storeRequest("records", "readwrite", (store) => store.put({ id: "offline-session", ...encrypted }));
}

export async function loadOfflineSession({ allowSignedOut = false } = {}) {
  if (!allowSignedOut && await isOfflineSignedOut()) return null;
  const record = await storeRequest("records", "readonly", (store) => store.get("offline-session"));
  return record ? unseal(record) : null;
}

export async function clearOfflineSession() {
  await storeRequest("records", "readwrite", (store) => {
    store.delete("offline-session");
    store.delete("offline-credential");
    store.put({ id: "offline-session-state", signed_out: true });
  });
  activeScope = "";
}

export async function markOfflineSignedOut() {
  await storeRequest("records", "readwrite", (store) => store.put({ id: "offline-session-state", signed_out: true }));
}

export async function clearOfflineSignedOut() {
  await storeRequest("records", "readwrite", (store) => store.delete("offline-session-state"));
}

export async function isOfflineSignedOut() {
  const state = await storeRequest("records", "readonly", (store) => store.get("offline-session-state"));
  return Boolean(state?.signed_out);
}

export async function saveOfflineCredential(identifier, password, session) {
  if (!session?.user?.user_id || !session?.tenant?.tenant_id || !identifier || !password || session.must_change_password) {
    await storeRequest("records", "readwrite", (store) => store.delete("offline-credential"));
    return;
  }
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const verifier = await derivePasswordVerifier(password, salt);
  const encrypted = await seal({
    identifier: identifier.trim().toLowerCase(),
    user_id: session.user.user_id,
    tenant_id: session.tenant.tenant_id,
    salt: encodeBase64(salt),
    verifier: encodeBase64(verifier),
  });
  await storeRequest("records", "readwrite", (store) => store.put({ id: "offline-credential", ...encrypted }));
}

export async function verifyOfflineCredential(identifier, password, session) {
  if (!identifier || !password || !session?.user?.user_id || !session?.tenant?.tenant_id || session.must_change_password) return false;
  const record = await storeRequest("records", "readonly", (store) => store.get("offline-credential"));
  if (!record) return false;
  const credential = await unseal(record);
  if (credential.identifier !== identifier.trim().toLowerCase() ||
      credential.user_id !== session.user.user_id || credential.tenant_id !== session.tenant.tenant_id) return false;
  const actual = await derivePasswordVerifier(password, decodeBase64(credential.salt));
  return matchesVerifier(actual, decodeBase64(credential.verifier));
}

function cacheId(config) {
  const url = new URL(config.url || "", config.baseURL || window.location.origin);
  Object.entries(config.params || {}).sort(([left], [right]) => left.localeCompare(right)).forEach(([key, value]) => {
    if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
  });
  return `${activeScope}:GET:${url.pathname}${url.search}`;
}

export async function cacheGetResponse(config, data) {
  if (!activeScope || config?.offlineCache === false) return;
  const encrypted = await seal({ data, cached_at: new Date().toISOString() });
  await storeRequest("records", "readwrite", (store) => store.put({ id: cacheId(config), ...encrypted }));
}

export async function loadCachedGetResponse(config) {
  if (!activeScope) return null;
  const record = await storeRequest("records", "readonly", (store) => store.get(cacheId(config)));
  return record ? unseal(record) : null;
}

export async function queueOfflineRequest(config, idempotencyKey) {
  if (!activeScope || !idempotencyKey) throw new Error("Offline writes require an active user and an idempotency key");
  if (typeof FormData !== "undefined" && config.data instanceof FormData) throw new Error("File uploads cannot be queued for offline sync");
  const request = {
    id: `${activeScope}:outbox:${idempotencyKey}`,
    scope: activeScope,
    idempotency_key: idempotencyKey,
    queued_at: new Date().toISOString(),
    method: String(config.method || "post").toLowerCase(),
    url: config.url,
    baseURL: config.baseURL,
    params: config.params,
    data: config.data,
    headers: config.headers?.toJSON ? config.headers.toJSON() : config.headers,
  };
  const encrypted = await seal(request);
  await storeRequest("records", "readwrite", (store) => store.put({ id: request.id, scope: activeScope, type: "outbox", ...encrypted }));
  notifyOfflineState();
  return { request_id: idempotencyKey, queued_at: request.queued_at };
}

export async function listOfflineQueue() {
  if (!activeScope) return [];
  const records = await storeRequest("records", "readonly", (store) => store.getAll());
  const queue = [];
  for (const record of records.filter((item) => item.type === "outbox" && item.scope === activeScope)) {
    queue.push(await unseal(record));
  }
  return queue.sort((left, right) => left.queued_at.localeCompare(right.queued_at));
}

export async function pendingOfflineCount() {
  return (await listOfflineQueue()).length;
}

export async function removeOfflineRequest(idempotencyKey) {
  if (!activeScope) return;
  await removeOfflineRequestForScope(activeScope, idempotencyKey);
}

async function removeOfflineRequestForScope(scope, idempotencyKey) {
  await storeRequest("records", "readwrite", (store) => store.delete(`${scope}:outbox:${idempotencyKey}`));
  notifyOfflineState();
}

export async function flushOfflineQueue(sendRequest) {
  if (!navigator.onLine || !activeScope) return { sent: 0, failed: 0 };
  const flushScope = activeScope;
  let sent = 0;
  let failed = 0;
  for (const request of await listOfflineQueue()) {
    if (!navigator.onLine || activeScope !== flushScope || request.scope !== flushScope) break;
    try {
      await sendRequest(request);
      await removeOfflineRequestForScope(flushScope, request.idempotency_key);
      sent += 1;
    } catch (error) {
      failed += 1;
      if (!error?.response) break;
    }
  }
  return { sent, failed };
}